import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ChevronRight,
  Search,
  X,
  Plus,
  Trash2,
  Check,
  Loader2,
  CalendarDays,
} from "lucide-react";
import {
  addDoc,
  updateDoc,
  arrayUnion,
  collection,
  getDoc,
  getDocs,
  doc,
  query,
  where,
  limit,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";
import SelectMenu from "../../components/crm/SelectMenu";
import {
  PRIORITY_META,
  REMINDER_OPTIONS,
  reminderLabel,
  initialsOf,
  avatarColor,
  fmtDueLong,
} from "./tasksShared";

// Local (not UTC) YYYY-MM-DD — toISOString() would roll back a day for IST
// users in the early hours and let "yesterday" through as a valid due date.
const todayISO = () => {
  const d = new Date();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

export default function AddTask() {
  const navigate = useNavigate();
  const { globalUser } = useAuth();
  const uid = globalUser?.uid;
  const [params] = useSearchParams();
  const presetStatus = params.get("status");
  const editId = params.get("edit");
  const isEdit = Boolean(editId);

  const [userName, setUserName] = useState("User");
  const [cases, setCases] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editStatus, setEditStatus] = useState("todo");

  const [title, setTitle] = useState("");
  const [caseId, setCaseId] = useState("");
  const [assignees, setAssignees] = useState([]); // [{ uid, name, email }]
  const [priority, setPriority] = useState("medium");
  const [dueDate, setDueDate] = useState("");
  // Due date of the task being edited. An older task can legitimately already
  // sit in the past, so editing it must not be blocked by the "no past dates"
  // rule — only a *changed* date has to be today or later.
  const [originalDueDate, setOriginalDueDate] = useState("");
  const [reminder, setReminder] = useState("1day");
  const [reminderAt, setReminderAt] = useState(""); // datetime-local, only for "custom"
  const [description, setDescription] = useState("");
  const [checklist, setChecklist] = useState([]); // [{ id, text, done }]

  // assignee search
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!uid) return;
    (async () => {
      try {
        const uSnap = await getDoc(doc(db, "lawyers", uid));
        if (uSnap.exists()) setUserName(uSnap.data().name || "User");
        const all = [];
        for (const col of ["pending", "disposed"]) {
          const snap = await getDocs(
            query(collection(db, col), where("owner", "==", uid))
          );
          snap.docs.forEach((d) => all.push({ id: d.id, ...d.data() }));
        }
        setCases(all);

        // Prefill the linked case when arriving from a case's Quick Actions.
        const presetCaseId = params.get("caseId");
        if (!editId && presetCaseId && all.some((c) => c.id === presetCaseId)) {
          setCaseId(presetCaseId);
        }

        // Edit mode: load the task and prefill.
        if (editId) {
          const tSnap = await getDoc(doc(db, "lawyers", uid, "tasks", editId));
          if (tSnap.exists()) {
            const t = tSnap.data();
            setTitle(t.title || "");
            setPriority(t.priority || "medium");
            setDueDate(t.dueDate || "");
            setOriginalDueDate(t.dueDate || "");
            setReminder(t.reminder || "none");
            setReminderAt(t.reminderAt || "");
            setDescription(t.description || "");
            setChecklist((t.checklist || []).map((c, i) => ({ id: c.id || `${i}`, text: c.text || "", done: !!c.done })));
            setEditStatus(t.status || "todo");
            if (Array.isArray(t.assignees) && t.assignees.length) {
              setAssignees(t.assignees.map((a) => ({ uid: a.uid, name: a.name || "", email: a.email || "" })));
            } else if (t.assigneeUid) {
              setAssignees([{ uid: t.assigneeUid, name: t.assigneeName || "", email: "" }]);
            }
            const match = all.find((c) => c.caseNo && c.caseNo === t.linkedCaseNo);
            if (match) setCaseId(match.id);
          } else {
            toast.error("Task not found");
            navigate("/portal/tasksevents");
          }
        }
      } catch (e) {
        console.error("AddTask load:", e);
      }
    })();
  }, [uid, editId]); // eslint-disable-line

  // Debounced member search for assignee.
  useEffect(() => {
    const h = setTimeout(async () => {
      const q = term.trim();
      if (!q) {
        setResults([]);
        return;
      }
      setSearching(true);
      try {
        const end = q + String.fromCharCode(0xf8ff);
        const byId = new Map();
        for (const attr of ["name", "email"]) {
          const snap = await getDocs(
            query(collection(db, "lawyers"), where(attr, ">=", q), where(attr, "<=", end), limit(8))
          );
          snap.forEach((d) => {
            if (d.id !== uid) byId.set(d.id, { uid: d.id, ...d.data() });
          });
        }
        setResults([...byId.values()]);
      } catch (e) {
        console.error("assignee search:", e);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(h);
  }, [term, uid]);

  const selectedCase = cases.find((c) => c.id === caseId) || null;
  const caseName = selectedCase
    ? [selectedCase.petitionerName, selectedCase.respondentName].filter(Boolean).join(" vs ") ||
      selectedCase.caseType ||
      ""
    : "";
  const caseLabel = selectedCase
    ? `${caseName}${selectedCase.caseNo ? ` - ${selectedCase.caseNo}` : ""}`
    : "";

  const addChecklistItem = () =>
    setChecklist((prev) => [...prev, { id: `${prev.length + 1}-${Date.now() % 100000}`, text: "", done: false }]);
  const updateChecklistItem = (id, patch) =>
    setChecklist((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  const removeChecklistItem = (id) =>
    setChecklist((prev) => prev.filter((it) => it.id !== id));

  const statusFromPreset = () => {
    if (presetStatus === "completed") return "completed";
    if (presetStatus === "inprogress") return "inprogress";
    return "todo"; // todo / overdue both start as todo (overdue is derived)
  };

  // Earliest date the picker accepts: today, or the task's own (already past)
  // due date when editing so an untouched old task stays saveable.
  const minDueDate =
    originalDueDate && originalDueDate < todayISO() ? originalDueDate : todayISO();

  const validate = () => {
    if (!title.trim()) return toast.error("Task title is required"), false;
    if (!dueDate) return toast.error("Pick a due date"), false;
    if (dueDate < minDueDate)
      return toast.error("Due date cannot be in the past"), false;
    if (reminder === "custom" && !reminderAt)
      return toast.error("Pick a date & time for the custom reminder"), false;
    return true;
  };

  const handleCreate = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const cleanChecklist = checklist
        .filter((c) => c.text.trim())
        .map((c) => ({ id: c.id, text: c.text.trim(), done: !!c.done }));
      const core = {
        type: "task",
        title: title.trim(),
        linkedCaseNo: selectedCase?.caseNo || "",
        linkedCaseName: caseName,
        assignees: assignees.map((a) => ({ uid: a.uid, name: a.name || "" })),
        // Legacy single-assignee fields (board/detail views read these) — first assignee.
        assigneeUid: assignees[0]?.uid || "",
        assigneeName: assignees[0]?.name || "",
        priority,
        dueDate,
        reminder,
        reminderAt: reminder === "custom" ? reminderAt : "",
        description: description.trim(),
        checklist: cleanChecklist,
        updatedAt: now,
      };

      const activityEntry = (text) => ({ id: `act-${Date.now()}`, text, byName: userName, byUid: uid, at: now });

      if (isEdit) {
        await updateDoc(doc(db, "lawyers", uid, "tasks", editId), {
          ...core,
          status: editStatus, // keep the task's current workflow status
          activity: arrayUnion(activityEntry("Updated the task")),
        });
        toast.success("Task updated");
        navigate(`/portal/task/${editId}`);
        return;
      }

      await addDoc(collection(db, "lawyers", uid, "tasks"), {
        ...core,
        status: statusFromPreset(),
        createdBy: userName,
        createdByUid: uid,
        createdAt: now,
        activity: [activityEntry("Created the task")],
        comments: [],
        attachments: [],
      });
      toast.success("Task created");
      navigate("/portal/tasksevents");
    } catch (e) {
      console.error("save task:", e);
      toast.error(isEdit ? "Failed to update task" : "Failed to create task");
    } finally {
      setSaving(false);
    }
  };

  const labelCls = "block text-sm font-medium text-slate-600 mb-1.5";
  const fieldCls =
    "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40";

  const previewRows = [
    { label: "Title", value: title || "—" },
    { label: "Linked Case", value: caseLabel || "—" },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-gray-900">
      <main className="px-4 sm:px-6 lg:px-8 py-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-sm">
          <Link to="/portal/tasksevents" className="text-slate-500 hover:text-slate-800">Tasks &amp; Events</Link>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <span className="text-slate-500">Tasks</span>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <span className="font-medium text-slate-800">{isEdit ? "Edit Task" : "Add New Task"}</span>
        </div>

        <div className="mt-2">
          <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">{isEdit ? "Edit Task" : "Add New Task"}</h1>
          <p className="mt-0.5 text-xs text-slate-500">{isEdit ? "Update task details" : "Create a new task and assign it to team members"}</p>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
          {/* ===== Form ===== */}
          <div className="space-y-4 lg:col-span-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div>
                <label className={labelCls}>Task Title <span className="text-red-500">*</span></label>
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Prepare Arguments for Hearing" className={fieldCls} />
              </div>

              <div className="mt-4">
                <label className={labelCls}>Linked Case</label>
                <SelectMenu value={caseId} onChange={(e) => setCaseId(e.target.value)} className={fieldCls}>
                  <option value="">No linked case</option>
                  {cases.map((c) => (
                    <option key={c.id} value={c.id}>
                      {[c.petitionerName, c.respondentName].filter(Boolean).join(" vs ")}{c.caseNo ? ` - ${c.caseNo}` : ""}
                    </option>
                  ))}
                </SelectMenu>
              </div>

              <div className="mt-4">
                <label className={labelCls}>Assignees</label>
                {assignees.length > 0 && (
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    {assignees.map((a) => (
                      <span key={a.uid} className="inline-flex items-center gap-2 rounded-lg bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700">
                        <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold ${avatarColor(a.name)}`}>
                          {initialsOf(a.name)}
                        </span>
                        {a.name}
                        <button
                          type="button"
                          onClick={() => setAssignees((prev) => prev.filter((x) => x.uid !== a.uid))}
                          className="text-indigo-400 hover:text-indigo-700"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="relative">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search member by name or email..." className={`${fieldCls} pl-9`} />
                  </div>
                  {(searching || results.length > 0) && (
                    <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white p-1 shadow-xl">
                      {searching && (
                        <div className="flex justify-center py-3 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /></div>
                      )}
                      {results
                        .filter((u) => !assignees.some((a) => a.uid === u.uid))
                        .map((u) => (
                          <button
                            key={u.uid}
                            type="button"
                            onClick={() => {
                              setAssignees((prev) => [...prev, { uid: u.uid, name: u.name || "", email: u.email || "" }]);
                              setTerm("");
                              setResults([]);
                            }}
                            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-slate-50"
                          >
                            <span className={`flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-semibold ${avatarColor(u.name)}`}>
                              {initialsOf(u.name)}
                            </span>
                            <span className="min-w-0">
                              <span className="block truncate text-sm font-medium text-slate-800">{u.name || "Unnamed"}</span>
                              <span className="block truncate text-xs text-slate-400">{u.email || ""}</span>
                            </span>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>Priority</label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full" style={{ backgroundColor: PRIORITY_META[priority].dot }} />
                    <SelectMenu value={priority} onChange={(e) => setPriority(e.target.value)} className={`${fieldCls} pl-8`}>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </SelectMenu>
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Due Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    value={dueDate}
                    min={minDueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className={`${fieldCls} [color-scheme:light]`}
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className={labelCls}>Reminder</label>
                <SelectMenu value={reminder} onChange={(e) => setReminder(e.target.value)} className={fieldCls}>
                  {REMINDER_OPTIONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </SelectMenu>
                {reminder === "custom" && (
                  <div className="mt-2">
                    <label className={labelCls}>Remind me on <span className="text-red-500">*</span></label>
                    <input
                      type="datetime-local"
                      value={reminderAt}
                      onChange={(e) => setReminderAt(e.target.value)}
                      className={`${fieldCls} [color-scheme:light]`}
                    />
                  </div>
                )}
              </div>

              <div className="mt-4">
                <label className={labelCls}>Description</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Add more details about this task..." className={`${fieldCls} resize-none`} />
              </div>
            </div>

            {/* Checklist */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-800">Checklist</h3>
              <div className="mt-3 space-y-2">
                {checklist.map((it) => (
                  <div key={it.id} className="flex items-center gap-2 rounded-lg border border-slate-100 px-3 py-2">
                    <input
                      type="checkbox"
                      checked={it.done}
                      onChange={(e) => updateChecklistItem(it.id, { done: e.target.checked })}
                      className="h-4 w-4 shrink-0 accent-emerald-500"
                    />
                    <input
                      value={it.text}
                      onChange={(e) => updateChecklistItem(it.id, { text: e.target.value })}
                      placeholder="Checklist item"
                      className="min-w-0 flex-grow border-none bg-transparent p-0 text-sm text-slate-800 focus:outline-none"
                    />
                    <button type="button" onClick={() => removeChecklistItem(it.id)} className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" onClick={addChecklistItem} className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:underline">
                <Plus className="h-4 w-4" /> Add Checklist Item
              </button>
            </div>

            {/* Actions (mobile-friendly, also mirrored under preview on desktop) */}
            <div className="flex items-center justify-end gap-2 lg:hidden">
              <button type="button" onClick={() => navigate("/portal/tasksevents")} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50">Cancel</button>
              <button type="button" disabled={saving} onClick={handleCreate} className="inline-flex items-center gap-2 rounded-lg bg-yellow-500 px-5 py-2 text-sm font-semibold text-gray-900 shadow-sm hover:bg-yellow-600 disabled:opacity-50">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {isEdit ? "Save Changes" : "Create Task"}
              </button>
            </div>
          </div>

          {/* ===== Preview ===== */}
          <div className="lg:col-span-1">
            <div className="sticky top-4">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <h4 className="text-base font-bold text-slate-900">Task Preview</h4>
                <div className="mt-4 space-y-3 text-sm">
                  {previewRows.map((r) => (
                    <div key={r.label}>
                      <p className="text-xs text-slate-400">{r.label}</p>
                      <p className="mt-0.5 font-medium text-slate-800">{r.value}</p>
                    </div>
                  ))}
                  <div>
                    <p className="text-xs text-slate-400">Assignees</p>
                    {assignees.length > 0 ? (
                      <div className="mt-0.5 space-y-1">
                        {assignees.map((a) => (
                          <p key={a.uid} className="flex items-center gap-2 font-medium text-slate-800">
                            <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold ${avatarColor(a.name)}`}>
                              {initialsOf(a.name)}
                            </span>
                            {a.name}
                          </p>
                        ))}
                      </div>
                    ) : <p className="mt-0.5 font-medium text-slate-800">—</p>}
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Priority</p>
                    <p className="mt-0.5 flex items-center gap-2 font-medium text-slate-800">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: PRIORITY_META[priority].dot }} />
                      {PRIORITY_META[priority].label}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Due Date</p>
                    <p className="mt-0.5 flex items-center gap-1.5 font-medium text-slate-800">
                      <CalendarDays className="h-4 w-4 text-slate-400" /> {dueDate ? fmtDueLong(dueDate) : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Reminder</p>
                    <p className="mt-0.5 font-medium text-slate-800">
                      {reminderLabel(reminder, reminderAt)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4 hidden items-center justify-end gap-2 lg:flex">
                <button type="button" onClick={() => navigate("/portal/tasksevents")} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50">Cancel</button>
                <button type="button" disabled={saving} onClick={handleCreate} className="inline-flex items-center gap-2 rounded-lg bg-yellow-500 px-5 py-2 text-sm font-semibold text-gray-900 shadow-sm hover:bg-yellow-600 disabled:opacity-50">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {isEdit ? "Save Changes" : "Create Task"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
